import localStorage from '@/util/localStorage';
import { PageContext, PageModePopoverType } from '@common/react/pageMode';
import { useCurrentUser } from '@common/react/user';
import { PageMode } from '@common/types';
import { useState } from 'react';

const NAV_PUBLIC_MINE_VIEW_TYPE = 'navPageMode';

// N.B. Keep this in sync with mobile/components/PageModeProvider.tsx
// This should probably become a reusable component across platforms.
export const PageContextProvider = ({
    children,
}: {
    children: React.ReactNode;
}) => {
    const { activePartnershipId } = useCurrentUser();
    const [pageMode, setPageMode] = useState<PageMode>(
        getInitialNavPageMode({ activePartnershipId })
    );
    const [popover, setPopover] = useState<{
        isOpen: boolean;
        hiddenAccountsCount: number;
        hiddenAccountsType: PageModePopoverType;
    }>({
        isOpen: false,
        hiddenAccountsCount: 0,
        hiddenAccountsType: 'private',
    });

    const showPopover = (count: number, type: PageModePopoverType) => {
        setPopover({
            isOpen: true,
            hiddenAccountsCount: count,
            hiddenAccountsType: type,
        });
    };

    const hidePopover = () => {
        setPopover((prev) => ({
            ...prev,
            isOpen: false,
        }));
    };

    const togglePageMode = () => {
        setPageMode(pageMode === 'PUBLIC' ? 'MINE' : 'PUBLIC');
    };

    return (
        <PageContext.Provider
            value={{
                pageMode,
                setPageMode: (v) => {
                    setPageMode(v);
                    localStorage.setItem(NAV_PUBLIC_MINE_VIEW_TYPE, v);
                },
                popover,
                showPopover,
                hidePopover,
                togglePageMode,
            }}
        >
            {children}
        </PageContext.Provider>
    );
};

const getInitialNavPageMode = ({
    activePartnershipId,
}: {
    activePartnershipId: string | null | undefined;
}): PageMode => {
    // If user does not have a partner, return MINE
    if (!activePartnershipId) return 'MINE';

    // If value is saved in local storage, return saved value
    const value = localStorage.getItem<unknown>(NAV_PUBLIC_MINE_VIEW_TYPE);
    if (value === 'MINE' || value === 'PUBLIC') return value;

    // Otherwise, return 'PUBLIC' because user has a partner
    return 'PUBLIC';
};
